<script lang="ts">
  import type { Component } from "svelte";
  import type { Attachment } from "svelte/attachments";
  import { PREVIEW_SHOWN } from "#lib/products/rows.ts";
  import type { RowPreview } from "#lib/products/rows.ts";
  import { fitOf } from "#lib/shots/bands.ts";
  import { SCENES } from "./scenes/index.ts";
  import Window from "./Window.svelte";

  let {
    title,
    body,
    plan = null,
    preview,
    short = false,
    testId,
  }: {
    title: string;
    body: string;
    plan?: string | null;
    preview: RowPreview;
    short?: boolean;
    testId: string;
  } = $props();

  const shown = $derived(PREVIEW_SHOWN[preview]);
  const wide = $derived(shown.wide === true);
  const scene = $derived(SCENES[preview]);
  const across = $derived(wide && scene !== undefined);

  let Scene = $state<Component | null>(null);

  const approach: Attachment<HTMLElement> = (node) => {
    const load = scene;
    if (load === undefined) return;
    const near = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        near.disconnect();
        void load().then((module) => (Scene = module.default));
      },
      { rootMargin: "50% 0px" },
    );
    near.observe(node);
    return () => near.disconnect();
  };
</script>

<article
  class={[
    "card flex flex-col overflow-hidden rounded-xl border border-edge bg-surface-1",
    wide && "md:col-span-full",
    across && "md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:items-center",
  ]}
  data-testid={testId}
>
  <div class={["relative z-10 flex flex-col gap-2 p-6 pb-0", across && "md:pb-6"]}>
    <span class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
      <h3 class="text-xl font-semibold tracking-tight text-ink-1">{title}</h3>
      {#if plan !== null}
        <span class="pill rounded-pill px-2 py-0.5 text-xs">{plan}</span>
      {/if}
    </span>
    <p class="min-h-12 max-w-lg text-md leading-relaxed text-ink-2">{body}</p>
  </div>
  {#if scene !== undefined}
    <div class={["relative m-6 h-64 sm:h-72", across && "md:h-88"]} {@attach approach}>
      {#if Scene !== null}
        <Scene />
      {:else}
        <div class="placeholder">
          <Window {preview} fit={fitOf(preview)} alt={title} />
        </div>
      {/if}
    </div>
  {:else}
    <div
      class={[
        "relative mx-6 mt-6",
        shown.tall === true ? "h-80 sm:h-112" : short ? "h-52" : "h-64 sm:h-72",
      ]}
    >
      <Window {preview} fit={fitOf(preview)} alt={title} />
    </div>
  {/if}
</article>

<style>
  .card {
    transition: border-color 200ms ease-out;
  }

  .card:hover {
    border-color: color-mix(in srgb, var(--tint, var(--ink-1)) 45%, var(--edge));
  }

  .placeholder {
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: var(--radius-card);
    --window-fade: 0;
  }

  .pill {
    background: color-mix(in srgb, var(--tint, var(--ink-3)) 22%, transparent);
    color: color-mix(in srgb, var(--tint, var(--ink-2)) 45%, var(--ink-1));
  }
</style>
